import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Check, 
  X, 
  ArrowRight, 
  Link2, 
  Sparkles,
  FileText,
  Share2,
  Users,
  Zap,
  Crown,
  Star,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { OnboardingUploadExperience } from "@/components/onboarding/OnboardingUploadExperience";
import { GmailImportSettings } from "@/components/onboarding/GmailImportSettings";
import { EasySyncOnboardingCard } from "@/components/onboarding/EasySyncOnboardingCard";
import { PLANS, formatLimit, isUnlimited, type PlanId } from "@/config/plans";

type Step = "plan" | "getStarted";

// Feature details for display
const FEATURE_DETAILS = {
  documents: { icon: FileText, label: "Documents" },
  sources: { icon: Link2, label: "Sources connectées" },
  aiSearch: { icon: Sparkles, label: "Recherches IA / jour" },
  pdfActions: { icon: Zap, label: "Actions PDF / mois" },
  shareLinks: { icon: Share2, label: "Liens de partage" },
  users: { icon: Users, label: "Utilisateurs" },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [currentStep, setCurrentStep] = useState<Step>("plan");
  const [selectedPlan, setSelectedPlan] = useState<PlanId | null>(null);
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [gmailSettingsOpen, setGmailSettingsOpen] = useState(false);
  const [powensLoading, setPowensLoading] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      setUserId(session.user.id);

      // Check if onboarding already completed
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_completed")
        .eq("id", session.user.id)
        .single();

      if (profile?.onboarding_completed) {
        navigate("/documents");
      }
    };
    checkAuth();
  }, [navigate]);

  const handlePlanSelect = async (planId: PlanId) => {
    if (!userId || loading) return;
    
    setSelectedPlan(planId);
    setLoading(true);
    
    try {
      // For paid plans, redirect to Stripe checkout immediately
      if (planId !== "discovery") {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: {
            planSlug: planId,
            billingCycle: isYearly ? "yearly" : "monthly",
            successUrl: `${window.location.origin}/onboarding?step=getStarted`,
            cancelUrl: `${window.location.origin}/onboarding`,
          },
        });

        if (error) throw error;
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("URL de paiement non reçue");
      }

      // For free plan, go to get started step immediately
      setCurrentStep("getStarted");
    } catch (error: any) {
      toast.error(error.message || "Une erreur est survenue");
      setSelectedPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      // Mark onboarding as complete
      await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", userId);

      toast.success("Bienvenue sur EasyFinder !");
      navigate("/documents");
    } catch (error: any) {
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = useCallback((count: number) => {
    setUploadedCount(prev => prev + count);
  }, []);

  // Check URL params for returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("step") === "getStarted") {
      setCurrentStep("getStarted");
      // Clean URL
      window.history.replaceState({}, "", "/onboarding");
    }
  }, []);

  const formatPrice = (plan: typeof PLANS.discovery) => {
    const price = isYearly ? plan.priceYearly : plan.priceMonthly;
    if (price === 0) return "Gratuit";
    
    if (isYearly) {
      return `${price}€/an`;
    }
    return `${price.toFixed(2).replace(".", ",")}€/mois`;
  };

  const getMonthlyEquivalent = (plan: typeof PLANS.discovery) => {
    if (plan.priceYearly === 0) return null;
    const monthly = plan.priceYearly / 12;
    return `soit ${monthly.toFixed(2).replace(".", ",")}€/mois`;
  };

  const renderPlanStep = () => (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/3 via-transparent to-accent/5" />
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3] 
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-primary/20 to-accent/10 rounded-full blur-[100px]" 
        />
        <motion.div 
          animate={{ 
            scale: [1.1, 1, 1.1],
            opacity: [0.2, 0.4, 0.2] 
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-gradient-to-br from-accent/15 to-primary/10 rounded-full blur-[80px]" 
        />
      </div>
      
      <div className="relative z-10 py-6 md:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Progress Indicator */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 mb-6"
          >
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center ring-4 ring-primary/20">
                <span className="text-xs font-bold text-white">1</span>
              </div>
              <span className="text-xs text-foreground font-medium ml-1">Plan</span>
            </div>
            <div className="w-12 h-px bg-border/50" />
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                <span className="text-xs font-medium text-muted-foreground">2</span>
              </div>
              <span className="text-xs text-muted-foreground ml-1">Import</span>
            </div>
          </motion.div>

          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-6 md:mb-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <Badge 
                variant="outline" 
                className="mb-4 px-4 py-1.5 text-xs font-medium border-primary/20 bg-primary/5 backdrop-blur-sm"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-primary" />
                Commencez gratuitement
              </Badge>
            </motion.div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
              <span className="bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent">
                Choisissez votre plan
              </span>
            </h1>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              Des outils puissants pour organiser vos documents
            </p>
          </motion.div>

          {/* Billing Toggle */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="flex flex-col items-center mb-8"
          >
            <div className="relative inline-flex items-center gap-1 bg-muted/60 backdrop-blur-xl rounded-xl p-1 border border-border/50 shadow-md">
              <motion.div
                className="absolute inset-y-1 rounded-lg bg-background shadow-sm"
                initial={false}
                animate={{
                  x: isYearly ? "calc(100% + 4px)" : "0%",
                  width: isYearly ? "calc(55% - 8px)" : "calc(45% - 8px)",
                }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
              <button
                onClick={() => setIsYearly(false)}
                className={`relative z-10 px-5 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  !isYearly ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`relative z-10 px-5 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${
                  isYearly ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"
                }`}
              >
                Annuel
                <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  -17%
                </span>
              </button>
            </div>
            <p className="text-xs text-foreground/60 mt-3 text-center flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-500" />
              Vous pourrez changer de plan à tout moment
            </p>
          </motion.div>

          {/* Paid Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 max-w-5xl mx-auto mb-6">
            {(['solo', 'famille', 'pro', 'business'] as PlanId[]).map((planId, index) => {
              const plan = PLANS[planId];
              const isSelected = selectedPlan === planId;
              const isPopular = planId === "pro";
              const isBusiness = planId === "business";
              const isFamille = planId === "famille";
              const isSolo = planId === "solo";
              
              // Get badge config
              const getBadgeConfig = () => {
                if (isPopular) return { 
                  gradient: "from-primary to-accent", 
                  icon: Star, 
                  label: "Populaire",
                  fill: true 
                };
                if (isBusiness) return { 
                  gradient: "from-amber-500 to-orange-500", 
                  icon: Crown, 
                  label: "Équipe",
                  fill: true 
                };
                if (isFamille) return { 
                  gradient: "from-emerald-500 to-teal-500", 
                  icon: Users, 
                  label: "Famille",
                  fill: false 
                };
                return { 
                  gradient: "from-blue-500 to-cyan-500", 
                  icon: Users, 
                  label: "Solo",
                  fill: false 
                };
              };
              
              const badgeConfig = getBadgeConfig();
              const BadgeIcon = badgeConfig.icon;
              
              // Get card border/bg config
              const getCardStyle = () => {
                if (isPopular) return "border-primary/50 bg-gradient-to-b from-primary/5 via-background to-background shadow-xl shadow-primary/10";
                if (isBusiness) return "border-amber-500/30 bg-gradient-to-b from-amber-500/5 via-background to-background hover:border-amber-500/50 hover:shadow-amber-500/10";
                if (isFamille) return "border-emerald-500/30 bg-gradient-to-b from-emerald-500/5 via-background to-background hover:border-emerald-500/50 hover:shadow-emerald-500/10";
                return "border-blue-500/30 bg-gradient-to-b from-blue-500/5 via-background to-background hover:border-blue-500/50 hover:shadow-blue-500/10";
              };
              
              // Get button style
              const getButtonStyle = () => {
                if (isPopular) return "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-400 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50";
                if (isBusiness) return "bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white shadow-md shadow-amber-500/20 hover:shadow-amber-500/40";
                if (isFamille) return "bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white shadow-md shadow-emerald-500/20 hover:shadow-emerald-500/40";
                return "bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white shadow-md shadow-blue-500/20 hover:shadow-blue-500/40";
              };
              
              // Get subtitle
              const getSubtitle = () => {
                if (isSolo) return "Usage quotidien";
                if (isFamille) return "Pour toute la famille";
                if (isPopular) return "Pour les pros";
                return "Pour les gros volumes";
              };
              
              return (
                <motion.div
                  key={planId}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    delay: 0.4 + index * 0.1,
                    duration: 0.5,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  className="relative group"
                >
                  {/* Popular Glow Effect */}
                  {isPopular && (
                    <motion.div 
                      animate={{ 
                        opacity: [0.4, 0.6, 0.4],
                        scale: [1, 1.02, 1]
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="absolute -inset-[2px] bg-gradient-to-r from-primary via-accent to-primary rounded-3xl blur-sm" 
                    />
                  )}
                  
                  <Card 
                    className={`group relative cursor-pointer transition-all duration-500 h-full overflow-hidden rounded-2xl backdrop-blur-sm ${
                      isSelected 
                        ? "ring-2 ring-primary shadow-2xl shadow-primary/20" 
                        : "hover:shadow-2xl hover:-translate-y-1"
                    } ${getCardStyle()}`}
                    onClick={() => handlePlanSelect(planId)}
                  >
                    {/* Badge */}
                    <div className="absolute top-0 right-0 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-x-0.5 group-hover:translate-y-0.5">
                      <div className={`
                        px-3 py-1 text-[10px] font-bold rounded-bl-xl transition-all duration-300 group-hover:shadow-lg
                        bg-gradient-to-r ${badgeConfig.gradient} text-white
                      `}>
                        <span className="flex items-center gap-1">
                          <BadgeIcon className={`w-2.5 h-2.5 ${badgeConfig.fill ? 'fill-white' : ''} transition-transform duration-300 group-hover:scale-110`} />
                          {badgeConfig.label}
                        </span>
                      </div>
                    </div>

                    <CardContent className="p-4 md:p-5">
                      {/* Plan Name */}
                      <div className="mb-3">
                        <h3 className="text-lg font-bold tracking-tight">{plan.name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {getSubtitle()}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="mb-4">
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold tracking-tight">
                            {isYearly ? plan.priceYearly : plan.priceMonthly.toFixed(2).replace(".", ",")}
                          </span>
                          <span className="text-muted-foreground text-xs font-medium">
                            €{isYearly ? "/an" : "/mois"}
                          </span>
                        </div>
                        {isYearly && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            soit {(plan.priceYearly / 12).toFixed(2).replace(".", ",")}€/mois
                          </p>
                        )}
                      </div>

                      {/* CTA Button */}
                      <Button 
                        className={`w-full mb-4 h-9 text-sm font-semibold rounded-lg transition-all duration-300 group ${
                          isPopular 
                            ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-400 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-[1.02]" 
                            : planId === "solo"
                              ? "bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white shadow-md shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-[1.02]"
                              : "bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white shadow-md shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-[1.02]"
                        }`}
                        variant="default"
                        disabled={loading && selectedPlan === planId}
                      >
                        {isSelected && loading ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <span className="flex items-center gap-1.5">
                            Souscrire
                            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        )}
                      </Button>

                      {/* Features */}
                      <div className="space-y-2.5">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                          Inclus
                        </p>
                        
                        <div className="space-y-1.5">
                          <FeatureItem 
                            icon={FileText}
                            label="Documents"
                            value={formatLimit(plan.limits.storage)}
                            iconColor="text-slate-500"
                          />
                          
                          <FeatureItem 
                            icon={Link2}
                            label="Sources"
                            value={isUnlimited(plan.limits.sources) ? "∞" : String(plan.limits.sources)}
                            iconColor="text-blue-500"
                          />
                          
                          <FeatureItem 
                            icon={Sparkles}
                            label="Recherches IA/jour"
                            value={plan.limits.aiDaily >= 300 ? "∞" : String(plan.limits.aiDaily)}
                            iconColor="text-violet-500"
                          />
                          <FeatureItem 
                            icon={Zap}
                            label="PDF/mois"
                            value={isUnlimited(plan.limits.pdfMonthly) ? "∞" : String(plan.limits.pdfMonthly)}
                            iconColor="text-orange-500"
                          />
                          <FeatureItem 
                            icon={Share2}
                            label="Partages"
                            value={isUnlimited(plan.limits.shareLinks) ? "∞" : String(plan.limits.shareLinks)}
                            iconColor="text-emerald-500"
                          />
                          <FeatureItem 
                            icon={Users}
                            label="Utilisateurs"
                            value={String(plan.limits.users)}
                            highlight={plan.limits.users > 1}
                            iconColor="text-pink-500"
                          />
                        </div>

                        {/* Extra Features */}
                        {(planId === "solo" || planId === "pro" || planId === "team" || planId === "business") && (
                          <div className="border-t border-border/50 pt-2.5 mt-2.5">
                            <div className="space-y-2.5">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <div className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 text-green-600" />
                                </div>
                                Support prioritaire
                              </div>
                              {plan.features.includes("export_zip") && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <div className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                                    <Check className="w-2.5 h-2.5 text-green-600" />
                                  </div>
                                  Export ZIP complet
                                </div>
                              )}
                              {plan.features.includes("roles_management") && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <div className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                                    <Check className="w-2.5 h-2.5 text-green-600" />
                                  </div>
                                  Gestion des rôles
                                </div>
                              )}
                              {plan.features.includes("audit_logs") && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <div className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                                    <Check className="w-2.5 h-2.5 text-green-600" />
                                  </div>
                                  Journal d'activité
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Discovery Plan - Subtle Section */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.4 }}
            className="max-w-xl mx-auto mt-6 mb-6"
          >
            <div 
              onClick={() => handlePlanSelect('discovery')}
              className={`relative p-4 rounded-xl border transition-all duration-300 cursor-pointer group ${
                selectedPlan === 'discovery'
                  ? 'border-emerald-500 bg-emerald-500/5 ring-1 ring-emerald-500/50'
                  : 'border-border/50 bg-muted/20 hover:border-emerald-500/30 hover:bg-emerald-500/5'
              }`}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-medium text-foreground text-sm">Rester sur le plan Découverte</h3>
                      <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-600 dark:text-emerald-400 px-1.5 py-0">
                        Gratuit
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {PLANS.discovery.limits.storage} documents • {PLANS.discovery.limits.aiDaily} recherches IA/jour • {PLANS.discovery.limits.pdfMonthly} actions PDF/mois
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="shrink-0 text-xs h-8 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/50"
                  disabled={loading && selectedPlan === 'discovery'}
                >
                  {selectedPlan === 'discovery' && loading ? (
                    <div className="w-3.5 h-3.5 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                  ) : (
                    <>
                      Continuer gratuitement
                      <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Footer Info */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex items-center justify-center gap-4 text-xs text-foreground/60"
          >
            <span className="flex items-center gap-1">
              <Check className="w-3.5 h-3.5 text-emerald-500" />
              Sans engagement
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3.5 h-3.5 text-emerald-500" />
              Annulation simple
            </span>
            <span className="flex items-center gap-1">
              <Shield className="w-3.5 h-3.5 text-emerald-500" />
              Paiement sécurisé
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  );

  const handleConnectGmail = async (settings: {
    periodMonths: number;
    maxSizeMb: number;
    smartImport: boolean;
    excludeNewsletters: boolean;
    attachmentsOnly: boolean;
  }) => {
    if (!userId) return;
    
    try {
      // Save import settings first
      await supabase.from("import_settings").upsert({
        user_id: userId,
        provider: "gmail" as const,
        period_months: settings.periodMonths,
        max_size_mb: settings.maxSizeMb,
        smart_import: settings.smartImport,
        exclude_newsletters: settings.excludeNewsletters,
        attachments_only: settings.attachmentsOnly,
      }, { onConflict: "user_id,provider" });

      // Then initiate OAuth
      const { data, error } = await supabase.functions.invoke("gmail-auth", {
        body: { redirectUrl: `${window.location.origin}/onboarding?step=getStarted` },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error("Gmail connection error:", error);
      toast.error("Erreur lors de la connexion Gmail");
    }
  };

  const handleConnectPowens = async () => {
    setPowensLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("powens-auth", {
        body: { redirectUrl: `${window.location.origin}/onboarding?step=getStarted` },
      });
      
      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("URL de connexion non reçue");
      }
    } catch (error: any) {
      console.error("Powens connection error:", error);
      toast.error("Erreur lors de l'initialisation de la connexion");
    } finally {
      setPowensLoading(false);
    }
  };

  const renderGetStartedStep = () => (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/3 via-transparent to-accent/5" />
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3] 
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-10 right-1/4 w-[400px] h-[400px] bg-gradient-to-br from-emerald-500/15 to-teal-500/10 rounded-full blur-[100px]" 
        />
      </div>

      <div className="relative z-10 py-6 md:py-10 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Progress Indicator */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-6 md:mb-8"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Check className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Plan</span>
            </div>
            <div className="w-12 h-px bg-primary/30" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center ring-4 ring-primary/20">
                <span className="text-xs font-bold text-white">2</span>
              </div>
              <span className="text-sm text-foreground font-medium">Import</span>
            </div>
          </motion.div>

          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 md:mb-10"
          >
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Importez vos premiers documents</h1>
            <p className="text-muted-foreground text-base md:text-lg">
              L'IA classe tout automatiquement • Vous pouvez aussi le faire plus tard
            </p>
          </motion.div>

          {/* Two Column Layout on Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left Column - Upload (Primary Action) */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full border-2 border-dashed border-primary/30 bg-primary/5">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-lg">Import manuel</h2>
                      <p className="text-sm text-muted-foreground">Glissez ou sélectionnez vos fichiers</p>
                    </div>
                  </div>
                  
                  {userId && (
                    <OnboardingUploadExperience 
                      userId={userId}
                      onComplete={handleUploadComplete}
                    />
                  )}
                  
                  <p className="text-sm text-emerald-600/90 dark:text-emerald-400/90 mt-4 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Vos fichiers restent privés et chiffrés
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Right Column - Automatic Sources */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">Ou connectez une source automatique</span>
              </div>

              {/* Gmail Card */}
              <Card 
                className="cursor-pointer border-border hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 group"
                onClick={() => setGmailSettingsOpen(true)}
              >
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm">
                    <svg className="w-7 h-7" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-base flex items-center gap-2">
                      Gmail
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 border-0">Recommandé</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">Importer automatiquement les pièces jointes de vos emails</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </CardContent>
              </Card>

              {/* EasySync Card */}
              <div>
                <EasySyncOnboardingCard 
                  onConnect={handleConnectPowens}
                  loading={powensLoading}
                />
              </div>

              <p className="text-sm text-emerald-600/90 dark:text-emerald-400/90 flex items-center gap-2 pt-2">
                <Shield className="w-4 h-4" />
                Accès lecture seule • Révocable à tout moment
              </p>
            </motion.div>
          </div>

          {/* Success Message */}
          {uploadedCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 flex items-center justify-center gap-2 text-base text-emerald-600 dark:text-emerald-400"
            >
              <Check className="w-5 h-5" />
              <span className="font-medium">{uploadedCount} document{uploadedCount > 1 ? "s" : ""} importé{uploadedCount > 1 ? "s" : ""}</span>
            </motion.div>
          )}

          {/* Action Button */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 md:mt-10 text-center"
          >
            <Button 
              size="lg" 
              onClick={handleComplete}
              disabled={loading}
              className={`h-12 px-8 text-base font-semibold rounded-xl transition-all duration-300 ${
                uploadedCount > 0 
                  ? "bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-lg shadow-primary/20"
                  : ""
              }`}
            >
              {uploadedCount > 0 ? (
                <>
                  Accéder au tableau de bord
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Passer cette étape
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            {uploadedCount === 0 && (
              <p className="text-sm text-muted-foreground mt-3">
                Vous pourrez importer des documents plus tard
              </p>
            )}
            
            {/* Security Footer */}
            <p className="mt-8 text-sm text-emerald-600/80 dark:text-emerald-400/80 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              Stockage chiffré en France • RGPD
            </p>
          </motion.div>
        </div>
      </div>

      {/* Gmail Settings Modal */}
      <GmailImportSettings
        open={gmailSettingsOpen}
        onOpenChange={setGmailSettingsOpen}
        onConnect={(settings) => {
          setGmailSettingsOpen(false);
          handleConnectGmail(settings);
        }}
      />
    </div>
  );

  return (
    <AnimatePresence mode="wait">
      {currentStep === "plan" && (
        <motion.div
          key="plan"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {renderPlanStep()}
        </motion.div>
      )}
      {currentStep === "getStarted" && (
        <motion.div
          key="getStarted"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {renderGetStartedStep()}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Helper component for feature rows
function FeatureRow({ included, label }: { included: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {included ? (
        <Check className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
      ) : (
        <X className="w-4 h-4 text-muted-foreground/50 shrink-0" />
      )}
      <span className={included ? "" : "text-muted-foreground/50"}>
        {label}
      </span>
    </div>
  );
}

// Helper component for feature items in plan cards
function FeatureItem({ 
  icon: Icon, 
  label, 
  value, 
  highlight = false,
  iconColor
}: { 
  icon: React.ComponentType<{ className?: string }>; 
  label: string; 
  value: string;
  highlight?: boolean;
  iconColor?: string;
}) {
  return (
    <div className="flex items-center justify-between text-xs group/feature">
      <span className="text-muted-foreground flex items-center gap-1.5 shrink-0">
        <Icon className={`w-3.5 h-3.5 transition-all duration-200 group-hover/feature:scale-125 ${iconColor || ""}`} />
        {label}
      </span>
      <span className={`font-semibold text-right ${highlight ? "text-primary" : ""}`}>
        {value}
      </span>
    </div>
  );
}
